/**
 * Product catalog data. Detail pages for: myfora, pay-at-table, pos-register,
 * pickup, videri-signage, loyalty, rms, pepe-ai — render through generic ProductPage.
 * digital-menu uses dedicated ProductDetail.js (with QR/Tablet/Stand modes).
 * fora-intelligence routes to the Why FORA — The Numbers dashboard (view: "data").
 */
import {
  TrendingUp, Users, Clock, Shield, CheckCircle2, Zap, Globe, Wifi, Database, Cpu,
  Bell, Calendar, MessageCircle, QrCode, Award, Gift, Sparkles, Smartphone, Layers,
  CreditCard, Megaphone, Activity, ToggleRight, FileText, UserCheck, Cloud, BarChart3,
  Smile, AlertTriangle, SplitSquareHorizontal, Coins, Maximize2, Leaf, Monitor,
  LayoutGrid, MessageSquare, ShoppingBag, MapPin, Link2, Settings, Brain, Tablet,
} from "lucide-react";

export const products = {
  myfora: {
    id: "myfora",
    name: "myFORA",
    subtitle: "Merchant Portal",
    category: "myFORA",
    tags: ["SW"],
    eyebrow: "myFORA — Merchant Portal",
    headline: "myFORA",
    hero_title: "One portal. Every FORA product. Activated in minutes.",
    description:
      "myFORA is the merchant cockpit where outlet owners and managers activate, configure and monitor every FORA product — Digital Menu, Pay at Table, Loyalty, Reservations, Signage and more. One login, one dashboard, full control over your venue's digital experience.",
    benefits: [
      { value: "1 portal", label: "All products in one place", icon: Layers },
      { value: "< 10 min", label: "Average product activation", icon: Zap },
      { value: "24/7", label: "Real-time monitoring", icon: Activity },
      { value: "Self-serve", label: "No IT team required", icon: ToggleRight },
    ],
    features: [
      { icon: ToggleRight, text: "Activate any FORA product with a single toggle — instant rollout" },
      { icon: FileText, text: "Manage menu, prices, allergens and assortment from one CMS" },
      { icon: UserCheck, text: "User & role management — owner, manager, staff, finance" },
      { icon: Activity, text: "Real-time outlet performance: orders, payments, loyalty, signage" },
      { icon: Cloud, text: "Cloud-native — works on any device, any browser" },
      { icon: Shield, text: "GDPR-compliant data handling and audit logs" },
    ],
    dependencies: [
      { name: "Digital Menu", desc: "Activate QR / Tablet / Stand menus, manage products, prices, allergens and translations from one CMS." },
      { name: "Pay at Table", desc: "Enable QR-based bill retrieval and payment for any outlet with a single switch." },
      { name: "FORA POS", desc: "Configure terminals, fiscal settings and operator accounts; sync data with myFORA reports." },
      { name: "Loyalty", desc: "Launch and manage loyalty campaigns, segment members, design rewards and track redemption." },
      { name: "RMS — Reservation", desc: "Set up table inventory, reservation rules, and walk-in flow for the outlet." },
      { name: "Videri Digital Signage", desc: "Schedule playlists, push creative to specific screen groups, monitor screen health." },
      { name: "Pepe — AI Sidekick", desc: "Tap into AI-powered insights and recommendations directly from the merchant dashboard." },
      { name: "FORA Intelligence", desc: "Drill into outlet-, market- and product-level analytics, exportable reports for finance and ops." },
    ],
    markets: ["HR", "PL", "BA", "SI", "MK", "RS", "RO"],
    gallery: [
      { src: "/myfora-hero.jpg", alt: "myFORA Merchant Portal on desktop, laptop and mobile", span: "col-span-2 md:col-span-7 row-span-3", bg: "bg-black", fit: "cover" },
      { src: "/myfora-poster.jpg", alt: "myFORA — Merchant Portal: One portal. Every FORA product. Activated in minutes.", span: "col-span-2 md:col-span-5 row-span-3", bg: "bg-black", fit: "contain" },
    ],
    quote: {
      text: "myFORA gives outlet managers the kind of self-service control that used to require an IT team and three vendors. One login, one source of truth, and the entire FORA stack at their fingertips.",
      author: "Master F&B",
      role: "Product Owner",
    },
    demo_url: "",
    schedule_subject: "FORA Digital Solutions — myFORA Merchant Portal",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss myFORA — the merchant portal — for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  "pay-at-table": {
    id: "pay-at-table",
    name: "Pay at Table",
    subtitle: "Order first, pay later",
    category: "Ordering & Payments",
    tags: ["SW"],
    eyebrow: "Ordering & Payments — Frictionless Checkout",
    headline: "Pay at Table",
    hero_title: "Order first, pay later — right from the table.",
    description:
      "Guests scan a QR code on the table to view the live bill, split it, leave a tip, and pay in seconds. No staff, no waiting. Real-time POS sync ensures every order, payment and tip lands directly in your existing system.",
    benefits: [
      { value: "-12 min", label: "Average table turnover", icon: Clock },
      { value: "+18%", label: "Tip rate vs. cash payment", icon: TrendingUp },
      { value: "100%", label: "Bills synced with POS", icon: CheckCircle2 },
      { value: "0 staff", label: "Required for checkout", icon: Users },
    ],
    features: [
      { icon: QrCode, text: "Table-based QR session opens the live bill instantly" },
      { icon: CheckCircle2, text: "Real-time bill retrieval from POS — always accurate" },
      { icon: SplitSquareHorizontal, text: "Split bills, partial payments, group splits" },
      { icon: Coins, text: "Add tip with one tap, suggested amounts and free input" },
      { icon: CreditCard, text: "Apple Pay, Google Pay, cards — all major methods" },
      { icon: Shield, text: "Payment confirmation synced back to POS, audit trail" },
    ],
    dependencies: [
      { name: "QR Menu", desc: "Pay at Table is built on top of the Digital Menu — the same QR session bridges browsing, ordering and paying." },
      { name: "Scan & Pay module", desc: "Standalone payment-only flow for outlets that don't yet need full ordering. Same checkout engine, simpler scope." },
      { name: "POS integration", desc: "Connects to your existing POS for real-time bill retrieval and payment reconciliation." },
      { name: "Ordering Panel", desc: "Optional digital ordering layer if you want guests to add items mid-meal before paying." },
      { name: "Payless scenarios", desc: "Frictionless premium flows: pre-authorized sessions, invisible checkout, group payments." },
    ],
    markets: ["HR", "PL", "BA", "SI"],
    gallery: [
      { src: "/pay-at-table-hero.jpg", alt: "Guest scanning Pay at Table QR cube with smartphone", span: "col-span-2 md:col-span-7 row-span-3", bg: "bg-black" },
      { src: "/qr-scan.png", alt: "Pay at Table mobile checkout UI", span: "col-span-2 md:col-span-5 row-span-3" },
    ],
    quote: null,
    demo_url: "https://qr.master-fb.com/6JC6ES7X",
    schedule_subject: "FORA Digital Solutions — Pay at Table",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss Pay at Table for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  "pos-register": {
    id: "pos-register",
    name: "FORA POS",
    subtitle: "Cloud-native POS register",
    category: "Ordering & Payments",
    tags: ["HW", "SW"],
    eyebrow: "Ordering & Payments — Point of Sale",
    headline: "FORA POS",
    hero_title: "One device. Every transaction. Zero friction.",
    description:
      "All-in-one cloud POS purpose-built for HoReCa. Take orders, accept any payment method, stay fiscally compliant, and sync every transaction in real time with the rest of the FORA stack — menu, loyalty, ordering and reporting.",
    benefits: [
      { value: "-15s", label: "Average order processing time", icon: Clock },
      { value: "+24%", label: "Upsell rate with smart prompts", icon: TrendingUp },
      { value: "99.9%", label: "Uptime, offline-resilient", icon: Shield },
      { value: "100%", label: "Fiscal-ready out of the box", icon: CheckCircle2 },
    ],
    features: [
      { icon: Cpu, text: "All-in-one terminal — hardware + software, plug & play" },
      { icon: CheckCircle2, text: "Built-in fiscalization — compliant in every CC HBC market" },
      { icon: CreditCard, text: "Cards, Apple/Google Pay, QR, cash — every method, one terminal" },
      { icon: Wifi, text: "Offline mode — keep selling, sync when the network returns" },
      { icon: Database, text: "Real-time inventory and recipe-level cost tracking" },
      { icon: Globe, text: "Multilingual UI for staff with multiple nationalities" },
    ],
    dependencies: [
      { name: "Digital Menu", desc: "Items, prices and modifiers stay in sync between the QR menu and the POS — edit once, deployed everywhere." },
      { name: "Pay at Table", desc: "Guests pay at the table via QR; payment is reconciled directly to the open bill in FORA POS." },
      { name: "Loyalty", desc: "Member identification at checkout, automated points accrual and redemption." },
      { name: "myFORA", desc: "Provision terminals, manage operators, view sales reports — all from the merchant portal." },
      { name: "FORA Intelligence", desc: "Every transaction feeds the live analytics dashboard for outlet- and market-level insights." },
      { name: "Payment terminal integration", desc: "Pre-integrated with the leading PSPs in each market — no separate device needed." },
    ],
    markets: ["HR", "PL"],
    gallery: [
      { src: "/fora-pos-hero.jpg", alt: "FORA POS terminal on counter with FORA branding", span: "col-span-2 md:col-span-7 row-span-2", bg: "bg-black" },
      { src: "/pos-register.avif", alt: "FORA POS hardware close-up", span: "col-span-2 md:col-span-5 row-span-2" },
    ],
    quote: null,
    demo_url: "",
    schedule_subject: "FORA Digital Solutions — FORA POS",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss FORA POS for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  pickup: {
    id: "pickup",
    name: "Pickup / Ordering",
    subtitle: "Off-premise revenue, without the headaches",
    category: "Ordering & Payments",
    tags: ["SW"],
    eyebrow: "Ordering & Payments — Online & Pickup",
    headline: "Pickup / Ordering",
    hero_title: "Open a second revenue stream — zero integrations.",
    description:
      "Branded online ordering and counter pickup for every outlet. Guests order from your custom page, payment is handled, and the order lands directly in your FORA POS — no manual entry, no third-party fees.",
    benefits: [
      { value: "+30%", label: "Off-premise revenue uplift", icon: TrendingUp },
      { value: "<60s", label: "Guest checkout time", icon: Clock },
      { value: "0", label: "Manual order entry", icon: Zap },
      { value: "24/7", label: "Always-on storefront", icon: Globe },
    ],
    features: [
      { icon: Globe, text: "Branded ordering page per outlet (custom logo, colors, slug)" },
      { icon: Smartphone, text: "Mobile-first responsive UX — zero install for guests" },
      { icon: Calendar, text: "Pickup time slots with capacity management" },
      { icon: CreditCard, text: "Online payment or pay-on-pickup — your choice" },
      { icon: Bell, text: "SMS / push notifications when the order is ready" },
      { icon: Link2, text: "Orders sync straight into FORA POS — no manual handling" },
    ],
    dependencies: [
      { name: "Digital Menu", desc: "Same menu, prices, allergens and translations as the in-venue QR menu — manage once." },
      { name: "FORA POS", desc: "Orders are delivered as tickets directly into FORA POS for kitchen execution and fiscalization." },
      { name: "Pay at Table", desc: "Same payment engine and PSP integration as table payments." },
      { name: "myFORA", desc: "Configure the ordering page, time slots, capacity and outlet hours from the merchant portal." },
      { name: "Loyalty", desc: "Members log in to earn and redeem points on pickup orders." },
    ],
    markets: ["HR"],
    gallery: [
      { src: "/pickup-hero.jpg", alt: "Pickup — guest grabbing online order at counter", span: "col-span-2 md:col-span-7 row-span-2", bg: "bg-black" },
      { src: "/myfora-hero.jpg", alt: "Order management in myFORA merchant portal", span: "col-span-2 md:col-span-5 row-span-2", bg: "bg-black" },
    ],
    quote: null,
    demo_url: "",
    schedule_subject: "FORA Digital Solutions — Pickup / Ordering",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss Pickup & Online Ordering for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  "videri-signage": {
    id: "videri-signage",
    name: "Videri",
    subtitle: "Digital Signage",
    category: "Engagement & Data",
    tags: ["HW", "SW", "SVC"],
    eyebrow: "Engagement & Data — Digital Signage",
    headline: "Videri",
    hero_title: "Be seen. Simply. Everywhere.",
    description:
      "Ultra-slim, fully wireless digital screens with an integrated media player. One flexible, all-in-one platform for simple installation, centrally managed content, and low-energy, sustainable brand activation in every outlet.",
    benefits: [
      { value: "+30%", label: "Sales uplift on promoted items", icon: TrendingUp },
      { value: "4x", label: "Higher customer engagement", icon: Users },
      { value: "-50%", label: "Lower energy consumption", icon: Leaf },
      { value: "100k+", label: "Screens deployed worldwide", icon: Monitor },
    ],
    features: [
      { icon: Maximize2, text: "Ultra-slim display — just 12.7 mm thin" },
      { icon: Cloud, text: "Integrated media player (Android™) with cloud CMS" },
      { icon: Wifi, text: "Fully wireless — no HDMI, USB or Ethernet required" },
      { icon: Shield, text: "Tamper-proof hardware, built-in security" },
      { icon: Layers, text: "All-in-one: device, cloud and content in one platform" },
      { icon: Smartphone, text: "Mobile-first orchestration for field teams" },
    ],
    dependencies: [
      { name: "Amazon Web Services", desc: "Enterprise-grade cloud infrastructure powering Videri's content delivery, device telemetry and scalability across every deployment." },
      { name: "Customized Android OS", desc: "A hardened Android build optimized for digital signage — low energy, fast boot, tamper-proof, remotely updatable over the air." },
      { name: "Mobile Application", desc: "Field-team companion app to preview campaigns, approve playlists and swap creative on any screen — from a phone on the shop floor." },
      { name: "Orchestration Studio", desc: "Central web console to schedule playlists, group screens by market or outlet, A/B test creatives and monitor network health in real time." },
      { name: "Videri Digital Screens", desc: "Purpose-built digital displays (20\", 33\", 34\", 44\", 50\") engineered for signage — ultra-slim, wireless, always-on, retail-ready." },
    ],
    markets: ["HR", "PL", "BA", "SI", "MK", "RS", "RO"],
    screen_sizes: ['20"', '33"', '34"', '44"', '50"'],
    spec_sheets: [
      {
        title: "Videri Spark Series — Specifications",
        description: "Full technical specifications for the Spark Series displays (20\", 33\", 34\", 44\", 50\"): dimensions, weight, connectivity, power, mounting options.",
        href: "/downloads/videri-spark-series-specifications.pdf",
        filename: "Videri-Spark-Series-Specifications.pdf",
        size: "1.9 MB",
        pages: "12 pages",
      },
    ],
    gallery: [
      { src: "/videri-hero.jpg", alt: "Videri digital signage across multiple touchpoints — pizza menu, Coca-Cola in-store, central media CMS", span: "col-span-2 md:col-span-7 row-span-3", bg: "bg-black" },
      { src: "/videri-cafe.jpg", alt: "Videri screens in a café with central Media CMS on laptop", span: "col-span-2 md:col-span-5 row-span-3", bg: "bg-black" },
    ],
    quote: {
      text: "Videri is a revolutionary technology — it transforms your digital visual strategy and reaches retail channels that were previously inaccessible for economic reasons. Your presence becomes unavoidable, your message more effective, and your audience more engaged than ever before.",
      author: "Anheuser-Busch",
      role: "Sr. Director, Marketing Culture and Learning",
    },
    demo_url: "",
    schedule_subject: "FORA Digital Solutions — Videri Digital Signage",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss Videri Digital Signage for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  loyalty: {
    id: "loyalty",
    name: "Loyalty",
    subtitle: "Turn every visit into the next one",
    category: "Engagement & Data",
    tags: ["SW", "SVC"],
    eyebrow: "Engagement & Data — Consumer Loyalty",
    headline: "Loyalty",
    hero_title: "Reward what you want to see more of.",
    description:
      "A consumer loyalty platform built for HoReCa. Automated rewards, GDPR-compliant data capture, and campaign tools that turn anonymous foot traffic into known, returning, higher-spending guests.",
    benefits: [
      { value: "3.2x", label: "Return visit frequency", icon: Users },
      { value: "+28%", label: "Customer lifetime value", icon: TrendingUp },
      { value: "40%", label: "Average member adoption", icon: Award },
      { value: "GDPR", label: "Fully compliant data capture", icon: Shield },
    ],
    features: [
      { icon: QrCode, text: "QR-based enrolment — join in under 10 seconds, no app required" },
      { icon: Award, text: "Tiered programmes (Bronze / Silver / Gold) with automated upgrades" },
      { icon: Gift, text: "Automated rewards — birthday drinks, X-th visit free, weekly perks" },
      { icon: Sparkles, text: "Personalised offers based on visit history and preferences" },
      { icon: MessageCircle, text: "Campaign builder — email + push, segmented by behavior" },
      { icon: Shield, text: "GDPR consent flow and data ownership built in" },
    ],
    dependencies: [
      { name: "Digital Menu", desc: "Member identification straight from the QR menu — see balance and redeem perks while ordering." },
      { name: "Pay at Table", desc: "Earn and redeem points at checkout, automatically applied to the bill." },
      { name: "FORA POS", desc: "Counter-side member lookup and redemption at the POS terminal." },
      { name: "myFORA", desc: "Design programmes, build campaigns, segment members and track ROI from the portal." },
      { name: "FORA Intelligence", desc: "Cohort analysis, churn risk, RFM segmentation — turn loyalty data into action." },
      { name: "Pepe — AI Sidekick", desc: "AI suggests the next-best offer per guest segment to maximize redemption and visit frequency." },
    ],
    markets: ["HR"],
    gallery: [
      { src: "/loyalty-hero.jpg", alt: "Handshake — Consumer loyalty", span: "col-span-2 md:col-span-7 row-span-2", bg: "bg-black" },
      { src: "/myfora-poster.jpg", alt: "Loyalty programme managed in myFORA portal", span: "col-span-2 md:col-span-5 row-span-2", bg: "bg-black" },
    ],
    quote: null,
    demo_url: "",
    schedule_subject: "FORA Digital Solutions — Loyalty",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss the Loyalty platform for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  rms: {
    id: "rms",
    name: "RMS — Reservation",
    subtitle: "Fill every table, every shift",
    category: "Engagement & Data",
    tags: ["SW"],
    eyebrow: "Engagement & Data — Reservation Management",
    headline: "RMS — Reservation",
    hero_title: "Real-time bookings. Walk-ins handled. Tables full.",
    description:
      "Online table booking, walk-in management and shift planning in one tool. Built for restaurants and bars that want to maximise covers without juggling phones, notebooks and spreadsheets.",
    benefits: [
      { value: "+35%", label: "More bookings vs. phone-only", icon: TrendingUp },
      { value: "-50%", label: "No-show rate with deposits", icon: AlertTriangle },
      { value: "Live", label: "Real-time table availability", icon: Clock },
      { value: "Hosts", label: "Mobile app for hostess teams", icon: Smartphone },
    ],
    features: [
      { icon: Calendar, text: "Online booking widget for your website and social channels" },
      { icon: LayoutGrid, text: "Visual table-layout designer — drag-and-drop floor plans" },
      { icon: Users, text: "Walk-in queue management with smart table assignment" },
      { icon: CreditCard, text: "Deposit and pre-authorization to fight no-shows" },
      { icon: MessageSquare, text: "Automated SMS / email confirmations and reminders" },
      { icon: Smartphone, text: "Hosts mobile app — manage the night from the phone in your apron" },
    ],
    dependencies: [
      { name: "Digital Menu", desc: "Pre-order option at booking time — guests can select dishes before they arrive." },
      { name: "FORA POS", desc: "Active reservations become tables in the POS — staff sees who is arriving when." },
      { name: "Pay at Table", desc: "Deposit and no-show fees are handled by the same payment engine." },
      { name: "myFORA", desc: "Manage availability, blackout dates, special menus and deposit rules from the portal." },
      { name: "Loyalty", desc: "Recognize returning guests at booking, offer perks to top members, route VIPs to best tables." },
    ],
    markets: ["HR"],
    gallery: [
      { src: "/rms-hero.jpg", alt: "FORA RMS dashboard on laptop and mobile", span: "col-span-2 md:col-span-7 row-span-2", bg: "bg-black" },
      { src: "/rms-photo.jpg", alt: "Guest checking in with mobile and receipt", span: "col-span-2 md:col-span-5", bg: "bg-black" },
      { src: "/rms-composite.jpg", alt: "Restaurant staff managing reservations on tablet", span: "col-span-2 md:col-span-5", bg: "bg-black" },
    ],
    quote: null,
    demo_url: "https://reserve.fora.solutions/en/venue/37",
    demo_label: "Try the booking widget (Rembrant)",
    schedule_subject: "FORA Digital Solutions — RMS Reservation",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss RMS — Reservation Management for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },

  "pepe-ai": {
    id: "pepe-ai",
    name: "Pepe — AI Sidekick",
    subtitle: "Your AI co-pilot for the venue",
    category: "Engagement & Data",
    tags: ["SW"],
    eyebrow: "Engagement & Data — AI Assistant",
    headline: "Pepe — AI Sidekick",
    hero_title: "Smarter decisions, every shift.",
    description:
      "Pepe is the AI assistant baked into the FORA stack. Ask plain-language questions about your venue, forecast demand for the weekend, spot anomalies in sales, optimise the menu — powered by real data from every other FORA product.",
    benefits: [
      { value: "+15%", label: "Gross margin uplift from menu tuning", icon: TrendingUp },
      { value: "92%", label: "Forecast accuracy (7-day demand)", icon: BarChart3 },
      { value: "24/7", label: "Always-on AI insights", icon: Brain },
      { value: "Natural", label: "Plain-language questions", icon: MessageCircle },
    ],
    features: [
      { icon: Sparkles, text: "Menu optimization — spot underperformers, recommend price changes" },
      { icon: BarChart3, text: "Demand forecasting per outlet, per day-part, per SKU" },
      { icon: AlertTriangle, text: "Anomaly detection — alerts when sales, cost or waste drift outside normal" },
      { icon: MessageCircle, text: "Natural language Q&A: \"What were my top 5 desserts last weekend?\"" },
      { icon: Brain, text: "Staff scheduling tips based on forecast traffic and skill mix" },
      { icon: Smile, text: "Guest sentiment from loyalty feedback and reviews, summarised weekly" },
    ],
    dependencies: [
      { name: "Digital Menu", desc: "Pepe reads your live menu structure to recommend pricing, positioning and cross-sell." },
      { name: "FORA POS", desc: "Transaction history feeds every forecast, anomaly model and margin recommendation." },
      { name: "Loyalty", desc: "Member behavior data unlocks segment-level insights and personalised recommendations." },
      { name: "RMS — Reservation", desc: "Booking pipeline feeds forecasting accuracy beyond historical sales alone." },
      { name: "FORA Intelligence", desc: "Drill from any Pepe insight into the underlying analytics dashboard." },
      { name: "myFORA", desc: "Pepe lives inside the merchant portal — always one click from where you work." },
    ],
    markets: ["HR"],
    gallery: [
      { src: "/myfora-hero.jpg", alt: "FORA Intelligence — analytics dashboard across devices", span: "col-span-2 md:col-span-7 row-span-2", bg: "bg-black" },
      { src: "/rms-hero.jpg", alt: "Real-time reservation & revenue insights", span: "col-span-2 md:col-span-5 row-span-2", bg: "bg-black" },
    ],
    quote: null,
    demo_url: "",
    schedule_subject: "FORA Digital Solutions — Pepe AI Sidekick",
    schedule_body:
      "Hi team,\n\nI'd like to schedule a meeting to discuss Pepe — the AI Sidekick — for our market.\n\nPlease suggest a few available time slots.\n\nBest regards",
    brief_pdf: "",
  },
};

export const getProduct = (id) => products[id];
